var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tooltip, Typography, Hidden } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { camelize } from '../../utils/camelize';
import { weightFormat } from '../../utils/format';
import Dialog from '../atoms/Dialog';
import Button from '../atoms/BaseButton';
import DeliveryRequestDatePicker from './DeliveryRequestDatePicker';
import MaterialTable from '../MaterialTable';
import QuantityField from '../atoms/QuantityField';
import DeliveryRequestItemsAndRequestDateDialogCard from './DeliveryRequestItemsAndRequestDateDialogCard';
var DeliveryRequestItemsAndRequestDateDialog = function (_a) {
    var roaster = _a.roaster, open = _a.open, onClose = _a.onClose, isFirstDelivery = _a.isFirstDelivery, installmentDeliveryRequestItemsAndRequestDates = _a.installmentDeliveryRequestItemsAndRequestDates, isLastInstallmentDeliveryRequestItemsAndRequestDate = _a.isLastInstallmentDeliveryRequestItemsAndRequestDate, requestItemsWithRequestableQuantity = _a.requestItemsWithRequestableQuantity, editItemsAndRequestDate = _a.editItemsAndRequestDate, addDeliveryRequestItemsAndRequestDate = _a.addDeliveryRequestItemsAndRequestDate, updateDeliveryRequestItemsAndRequestDate = _a.updateDeliveryRequestItemsAndRequestDate;
    var t = useTranslation().t;
    var theme = useTheme();
    var _b = React.useState({
        key: '',
        requestDate: null,
        isFastestDelivery: true,
        itemsAttributes: [],
    }), deliveryRequestGroupItemsAndRequestDate = _b[0], setDeliveryRequestGroupItemsAndRequestDate = _b[1];
    React.useEffect(function () {
        if (editItemsAndRequestDate)
            return setDeliveryRequestGroupItemsAndRequestDate(editItemsAndRequestDate);
        setDeliveryRequestGroupItemsAndRequestDate({
            key: "installment-".concat(Date.now()),
            requestDate: null,
            isFastestDelivery: isFirstDelivery,
            itemsAttributes: requestItemsWithRequestableQuantity.map(function (_a) {
                var orderedItemId = _a.orderedItemId, warehouseItemId = _a.warehouseItemId, requestableQuantity = _a.requestableQuantity, lotNumber = _a.lotNumber, bagSize = _a.bagSize;
                return ({
                    orderedItemId: orderedItemId,
                    warehouseItemId: warehouseItemId,
                    lotNumber: lotNumber,
                    bagSize: bagSize,
                    quantity: isLastInstallmentDeliveryRequestItemsAndRequestDate
                        ? requestableQuantity
                        : 0,
                });
            }),
        });
    }, [
        installmentDeliveryRequestItemsAndRequestDates,
        isLastInstallmentDeliveryRequestItemsAndRequestDate,
        requestItemsWithRequestableQuantity,
        editItemsAndRequestDate,
    ]);
    var upsertDeliveryRequestItemsAndRequestDate = React.useCallback(function (deliveryRequestGroupItemsAndRequestDate) {
        editItemsAndRequestDate
            ? updateDeliveryRequestItemsAndRequestDate(deliveryRequestGroupItemsAndRequestDate)
            : addDeliveryRequestItemsAndRequestDate(deliveryRequestGroupItemsAndRequestDate);
    }, [
        editItemsAndRequestDate,
        updateDeliveryRequestItemsAndRequestDate,
        addDeliveryRequestItemsAndRequestDate,
    ]);
    var isTargetItemAttributes = function (itemAttributes, newAttribute) {
        return newAttribute.orderedItemId
            ? itemAttributes.orderedItemId === newAttribute.orderedItemId
            : itemAttributes.warehouseItemId === newAttribute.warehouseItemId;
    };
    var itemsAttributesChange = React.useCallback(function (newAttribute) {
        var newItemsAttributes = deliveryRequestGroupItemsAndRequestDate.itemsAttributes.map(function (itemAttributes) {
            return isTargetItemAttributes(itemAttributes, newAttribute)
                ? newAttribute
                : itemAttributes;
        });
        setDeliveryRequestGroupItemsAndRequestDate(__assign(__assign({}, deliveryRequestGroupItemsAndRequestDate), { itemsAttributes: newItemsAttributes }));
    }, [deliveryRequestGroupItemsAndRequestDate]);
    var headerColumn = React.useMemo(function () { return ({
        title: t('params.offer.lotNumber'),
        render: function (_a) {
            var lotNumber = _a.lotNumber;
            return lotNumber;
        },
    }); }, [t]);
    var contentColumns = React.useMemo(function () { return [
        {
            title: t('params.offerList.status'),
            render: function (_a) {
                var roasterTransportToWarehousesStatus = _a.roasterTransportToWarehousesStatus;
                if (!roasterTransportToWarehousesStatus)
                    return;
                return t("params.transportToWarehouse.statuses.".concat(camelize(roasterTransportToWarehousesStatus)));
            },
        },
        {
            title: t('params.warehouseItem.remainingQuantity'),
            render: function (_a) {
                var roasterTransportToWarehousesStatus = _a.roasterTransportToWarehousesStatus, remainingQuantity = _a.remainingQuantity, bagSize = _a.bagSize;
                return roasterTransportToWarehousesStatus === 'warehousing'
                    ? "".concat(remainingQuantity, " (").concat(weightFormat(parseFloat(bagSize) * remainingQuantity), ")")
                    : 0;
            },
        },
        {
            title: t('params.warehouseItem.requestableQuantity'),
            render: function (_a) {
                var requestableQuantity = _a.requestableQuantity, bagSize = _a.bagSize;
                return "".concat(requestableQuantity, " (").concat(weightFormat(parseFloat(bagSize) * requestableQuantity), ")");
            },
        },
    ]; }, [t]);
    var actionColumn = React.useMemo(function () { return ({
        title: t('params.deliveryRequestItem.quantity'),
        render: function (requestItem) {
            var _a;
            var max = requestItem.requestableQuantity;
            var editItemsAndRequestDateAttribute = (_a = editItemsAndRequestDate === null || editItemsAndRequestDate === void 0 ? void 0 : editItemsAndRequestDate.itemsAttributes) === null || _a === void 0 ? void 0 : _a.find(function (itemAttributes) {
                return isTargetItemAttributes(itemAttributes, requestItem);
            });
            if (editItemsAndRequestDate && editItemsAndRequestDateAttribute) {
                max = max + editItemsAndRequestDateAttribute.quantity;
            }
            var currentItemsAttribute = deliveryRequestGroupItemsAndRequestDate.itemsAttributes.find(function (itemAttributes) {
                return isTargetItemAttributes(itemAttributes, requestItem);
            });
            return requestItem.roasterTransportToWarehousesStatus ===
                'warehousing' ? (React.createElement(QuantityField, { value: (currentItemsAttribute === null || currentItemsAttribute === void 0 ? void 0 : currentItemsAttribute.quantity) || 0, onChange: function (value) {
                    if ((!requestItem.orderedItemId && !requestItem.warehouseItemId) ||
                        value === undefined)
                        return;
                    itemsAttributesChange({
                        id: currentItemsAttribute === null || currentItemsAttribute === void 0 ? void 0 : currentItemsAttribute.id,
                        orderedItemId: requestItem.orderedItemId,
                        warehouseItemId: requestItem.warehouseItemId,
                        quantity: value,
                        lotNumber: requestItem.lotNumber,
                        bagSize: requestItem.bagSize,
                    });
                }, min: 0, max: max, disabled: (!editItemsAndRequestDate &&
                    isLastInstallmentDeliveryRequestItemsAndRequestDate) ||
                    max <= 0 })) : (React.createElement(Tooltip, { title: React.createElement(React.Fragment, null,
                    React.createElement(Typography, { variant: "body1" }, t('pages.typicaSelection.tooltip.isNotWarehousing.body1')),
                    React.createElement(Typography, { variant: "body2" }, t('pages.typicaSelection.tooltip.isNotWarehousing.body2'))), enterTouchDelay: 100 },
                React.createElement("div", null,
                    React.createElement(QuantityField, { value: 0, max: 0, onChange: function () { return undefined; }, disabled: true }))));
        },
    }); }, [
        t,
        editItemsAndRequestDate,
        itemsAttributesChange,
        deliveryRequestGroupItemsAndRequestDate,
        isLastInstallmentDeliveryRequestItemsAndRequestDate,
    ]);
    var columns = React.useMemo(function () { return [headerColumn].concat(contentColumns, [actionColumn]); }, [headerColumn, contentColumns, actionColumn]);
    var isValid = React.useMemo(function () {
        if (!deliveryRequestGroupItemsAndRequestDate.isFastestDelivery &&
            !deliveryRequestGroupItemsAndRequestDate.requestDate) {
            return false;
        }
        return (deliveryRequestGroupItemsAndRequestDate.itemsAttributes.reduce(function (accumulator, _a) {
            var quantity = _a.quantity;
            return accumulator + quantity;
        }, 0) > 0);
    }, [deliveryRequestGroupItemsAndRequestDate]);
    return (React.createElement(Dialog, { title: t('pages.deliveryRequestItemsAndRequestDateForm.dialog.title'), open: open, onClose: onClose, actions: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: onClose, variant: "outlined", color: "secondary" }, t('buttons.cancel')),
            isValid ? (React.createElement("div", null,
                React.createElement(Button, { onClick: function () {
                        upsertDeliveryRequestItemsAndRequestDate(deliveryRequestGroupItemsAndRequestDate);
                        onClose();
                    }, color: "primary", sx: { ml: 2 } }, t(editItemsAndRequestDate ? 'edit' : 'buttons.addItem')))) : (React.createElement(Tooltip, { title: t('pages.typicaSelection.tooltip.inValidDeliveryRequestItemsAndRequestDate'), enterTouchDelay: 100, placement: 'top-start' },
                React.createElement("div", null,
                    React.createElement(Button, { color: "primary", disabled: true, sx: { ml: 2 } }, t(editItemsAndRequestDate ? 'edit' : 'buttons.addItem')))))) },
        React.createElement(Box, { sx: { mb: 3 } },
            React.createElement(Hidden, { mdDown: true },
                React.createElement(MaterialTable, { model: '', title: '', columns: columns, data: requestItemsWithRequestableQuantity, options: { minBodyHeight: 100 } })),
            React.createElement(Hidden, { mdUp: true }, requestItemsWithRequestableQuantity.map(function (requestItem) {
                return (React.createElement(DeliveryRequestItemsAndRequestDateDialogCard, { key: requestItem.lotNumber, headerColumn: headerColumn, columns: contentColumns, actionColumn: actionColumn, requestItemWithRequestableQuantity: requestItem }));
            }))),
        React.createElement(DeliveryRequestDatePicker, { roaster: roaster, deliveryRequestGroupItemsAndRequestDate: deliveryRequestGroupItemsAndRequestDate, fastestSelectable: isFirstDelivery, value: deliveryRequestGroupItemsAndRequestDate.requestDate, onChange: function (requestDate, isFastestDelivery) {
                setDeliveryRequestGroupItemsAndRequestDate(__assign(__assign({}, deliveryRequestGroupItemsAndRequestDate), { requestDate: requestDate, isFastestDelivery: isFastestDelivery }));
            }, dialogProps: { style: { zIndex: theme.zIndex.modal + 10 } } })));
};
export default DeliveryRequestItemsAndRequestDateDialog;
